import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";

export interface SelectGroupConfiguration<Option> {

    label: string;

    valuesCallback: () => Option[];

    optionLabelCallback: (option: Option, index?: number) => string;

    selectedCallback: (option: Option, index?: number) => boolean;

    compareOptionsCallback: (a: Option, b: Option) => boolean;

    handleSelectionCallback: (option: Option, selected: boolean) => void;

    retrieveChildOptionsFromParentCallback: (option: Option) => Option[];

    allOptionsSelectionEnabledCallback: () => boolean;

    openChildOptionsOnParentSelectionCallback?: () => boolean;

    isSelectionRequiredCallback?: () => boolean;

    allOptionsLabelCallback?: () => string;

    allOptionsSelectedCallback?: () => boolean;

    allOptionsValueCallback?: () => unknown;

    handleAllOptionsSelectionCallback?: (parentOptions: Option[], selected: boolean) => void;

    fieldValueCallback?: (option: Option) => unknown; // Utilisé pour les filtres

    identifyOptionCallback?: (option: Option) => unknown; // Utilisé pour les filtres
}

@Component({
    selector: 'app-core-select-group',
    templateUrl: './select-group.component.html',
    styleUrl: './select-group.component.scss',
    standalone: false
})
export class SelectGroupComponent<Option> implements OnInit {

    @Input() configuration: Partial<SelectGroupConfiguration<Option>>;

    @Input() sourceCallback: () => Observable<Option[]>;

    @Output() selectionChange: EventEmitter<Option[]> = new EventEmitter();

    private _parentOptions$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);

    private _openedParentOptionIndexes: number[] = [];

    ngOnInit() {

        this.sourceCallback().subscribe((options: Option[]): void => {

            this._parentOptions$.next(options);
        });
    }

    public handleAllParents(selected: boolean): void {

        this.configuration.handleAllOptionsSelectionCallback(this.parentOptions, selected);

        this.parentOptions.forEach((parentOption: Option): void => {

            const childOptions: Option[] = this.configuration.retrieveChildOptionsFromParentCallback(parentOption);

            childOptions.forEach((childOption: Option): void => {

                this.configuration.handleSelectionCallback(childOption, selected);
            });
        });

        this.selectionChange.emit(this.values);
    }

    public toggleParentOptionPanelStatus(index: number, event: MouseEvent): void {

        event.stopPropagation();

        this.setParentOptionPanelStatus(index, !this.isParentOptionPanelOpened(index));
    }

    public setParentOptionPanelStatus(index: number, opened: boolean): void {

        const matchIndex: number = this._openedParentOptionIndexes.indexOf(index);

        if(opened && matchIndex < 0){

            this._openedParentOptionIndexes.push(index);
        }
        else if(!opened && matchIndex >= 0){

            this._openedParentOptionIndexes.splice(matchIndex, 1);
        }
    }

    public isParentOptionPanelOpened(index: number): boolean {

        return this._openedParentOptionIndexes.includes(index);
    }

    public handleParent(parentOption: Option, selected: boolean, parentIndex: number): void {

        this.configuration.handleSelectionCallback(parentOption, selected);

        const isAutoOpenEnabled: boolean = !!this.configuration.openChildOptionsOnParentSelectionCallback && this.configuration.openChildOptionsOnParentSelectionCallback();

        if(isAutoOpenEnabled && selected && this.configuration.retrieveChildOptionsFromParentCallback(parentOption).length){

            this.setParentOptionPanelStatus(parentIndex, true);
        }

        if(selected){

            const childOptions: Option[] = this.configuration.retrieveChildOptionsFromParentCallback(parentOption).filter((childOption: Option): boolean => {

                return !this.configuration.selectedCallback(childOption);
            });

            childOptions.forEach((childOption: Option): void => {

                this.configuration.handleSelectionCallback(childOption, true);
            });
        }
        else{

            const childOptions: Option[] = this.configuration.retrieveChildOptionsFromParentCallback(parentOption).filter((childOption: Option): boolean => {

                return this.configuration.selectedCallback(childOption);
            });

            childOptions.forEach((childOption: Option): void => {

                this.configuration.handleSelectionCallback(childOption, false);
            });
        }

        this.selectionChange.emit(this.values);
    }

    public handleChild(childOption: Option, parentOption: Option, selected: boolean): void {

        this.configuration.handleSelectionCallback(childOption, selected);

        if(selected){

            if(!this.configuration.selectedCallback(parentOption)){

                this.configuration.handleSelectionCallback(parentOption, true);
            }
        }
        else{

            const isEmptyChildSelection: boolean = this.configuration.retrieveChildOptionsFromParentCallback(parentOption).every((childOption: Option): boolean => {

                return !this.configuration.selectedCallback(childOption);
            });

            if(this.configuration.selectedCallback(parentOption) && isEmptyChildSelection){

                this.configuration.handleSelectionCallback(parentOption, false);
            }
        }

        this.selectionChange.emit(this.values);
    }

    public childOptionStyles(parentIndex: number): { [p: string]: string } {

        return {
            'display': this.isParentOptionPanelOpened(parentIndex) ? 'block' : 'none'
        };
    }

    public toggleParentOptionPanelClasses(parentIndex: number): { [p: string]: boolean } {

        return {
            'open': this.isParentOptionPanelOpened(parentIndex)
        };
    }

    get values(): Option[] {

        return this.configuration.valuesCallback();
    }

    set parentOptions(options: Option[]) {

        this._parentOptions$.next(options);
    }

    get parentOptions(): Option[] {

        return this._parentOptions$.getValue();
    }
}
